import { useState } from "react";
import { api } from "../api.ts";
import { t } from "../i18n.ts";
import { errorText, useMeta } from "../meta.ts";
import { useSync } from "../store.ts";
import { hapticTap, shareLink } from "../telegram.ts";
import { Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

const ROLE_ICON = { admin: "👑", adult: "🧑", child: "🧒" } as const;

interface Invite {
  role: "adult" | "child";
  code: string;
  link: string | null;
  expiresAt: string;
}

/** Члены семьи и приглашения (ссылка, которой можно поделиться в Telegram). */
export function FamilySheet({ onClose }: { onClose: () => void }) {
  const { me } = useSync();
  const meta = useMeta();
  const [invite, setInvite] = useState<Invite | null>(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = me?.role === "admin";

  const create = async (role: "adult" | "child") => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.invite(role);
      hapticTap();
      setInvite({ role, ...res });
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    if (!invite) return;
    if (!invite.link) return copy();
    const res = await shareLink(invite.link, t("inviteText"));
    if (res === "copied") toast(t("copied"));
  };

  const copy = async () => {
    if (!invite) return;
    const text = invite.link ?? `${t("inviteText")}: ${invite.code}`;
    const ok = await (navigator.clipboard?.writeText(text).then(() => true) ?? Promise.resolve(false)).catch(() => false);
    toast(ok ? t("copied") : t("copyFailed"));
  };

  const expires = invite ? new Date(invite.expiresAt) : null;

  return (
    <Sheet title={`👨‍👩‍👧 ${me?.familyName ?? t("familyTitle")}`} onClose={onClose}>
      <ul className="row-list">
        {(meta.data?.members ?? []).map((m) => (
          <li key={m.userId} className="row">
            <span className="row-icon" aria-hidden="true">
              {ROLE_ICON[m.role]}
            </span>
            <div className="row-body">
              <div className="row-title">
                {m.name}
                {m.isMe && <span className="muted"> · {t("you")}</span>}
              </div>
              <div className="row-meta">
                {t(`role.${m.role}`)}
                {m.username ? ` · @${m.username}` : ""}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {isAdmin ? (
        <div className="form add-form">
          <div className="field-row">
            <button type="button" className="btn" disabled={busy} onClick={() => void create("adult")}>
              🧑 {t("inviteAdult")}
            </button>
            <button type="button" className="btn" disabled={busy} onClick={() => void create("child")}>
              🧒 {t("inviteChild")}
            </button>
          </div>
          {invite && expires && (
            <div className="invite-card">
              <div className="invite-title">
                {ROLE_ICON[invite.role]} {invite.role === "child" ? t("inviteChild") : t("inviteAdult")}
              </div>
              <p className="hint">{t("inviteReady", { date: `${String(expires.getDate()).padStart(2, "0")}.${String(expires.getMonth() + 1).padStart(2, "0")}` })}</p>
              {invite.link && <div className="invite-link">{invite.link}</div>}
              <p className="hint">{t("inviteCode", { code: invite.code })}</p>
              <div className="field-row">
                <button type="button" className="btn btn-primary" onClick={() => void share()}>
                  📤 {t("share")}
                </button>
                <button type="button" className="btn" onClick={() => void copy()}>
                  📋 {t("copy")}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="hint">{t("onlyAdminInvites")}</p>
      )}
    </Sheet>
  );
}
